import { getSABAppInstance_Normal } from './shopify-app-bridge/app';
import { getSessionToken } from '@shopify/app-bridge/utilities';
import { store } from 'src/stores/store';
import apiService from 'src/services/api-service';
import { ADDONS_INFO_V4 } from 'src/constants/subscription';
import { getDiscountCodeValue } from 'src/helpers/subscription-v3-helper';

// Sends the identify data via GTM dataLayer instead of calling posthog.identify() directly.
// Configure a GTM tag (e.g. PostHog Tag Template) with a Custom Event trigger on `identify`
// to forward these properties to PostHog (or any other tool wired in GTM).
// See src/integrations/posthog.js for the original direct-SDK implementation (kept as backup).
const initGtmIdentify = async tenantApp => {
  const app = getSABAppInstance_Normal();

  const token = await getSessionToken(app);

  const [response, addOnNames, discountInfo] = await Promise.all([
    decodeToken(token),
    fetchSubscribedAddonNames(tenantApp),
    fetchAppliedDiscountCode()
  ]);

  const email = tenantApp?.externalInfo?.email || tenantApp?.optEmail || '';
  const externalId = tenantApp?.externalInfo?.id || '';
  const distinctId = response?.sub || externalId || email || 'anonymous_user';
  const userId = 'boostcommerce_' + distinctId;
  const groupId = 'boostcommerce_' + externalId;
  apiService.setUserId(distinctId);

  const chargeInfoActive = tenantApp?.chargeInfoActive || null;

  const traits = {
    distinct_id: userId,
    group_id: groupId,
    email,
    company: {
      id: externalId,
      name: tenantApp?.externalInfo?.name || '',
      domain: tenantApp?.externalInfo?.domain || '',
      type: tenantApp?.externalInfo?.plan_name || '',
      shop_id: tenantApp?.externalInfo?.myshopify_domain || ''
    },
    plan: chargeInfoActive
      ? {
          name: chargeInfoActive?.name || '',
          value: chargeInfoActive?.price || 0,
          currency: 'USD',
          status: 'active',
          subscribed_at: chargeInfoActive?.activated_at || '',
          cancelled_at: null,
          add_ons: addOnNames,
          discount: discountInfo
            ? {
                code: discountInfo?.code || '',
                value: getDiscountCodeValue(discountInfo).dcPostfixValue || '',
                expires_at: discountInfo?.expiredAt
                  ? new Date(discountInfo.expiredAt * 1000).toISOString()
                  : ''
              }
            : {}
        }
      : {},
    locale: tenantApp?.externalInfo?.primary_locale || '',
    integrations: []
  };

  const context = {
    app: 'boostcommerce',
    platform: 'shopify-client',
    distinct_id: userId,
    group_id: groupId,
    traits: {
      email,
      name: tenantApp?.externalInfo?.name || ''
    }
  };

  try {
    const payload = {
      event: 'identify',
      traits,
      context
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    console.log('GTM identify event pushed:', payload);
  } catch (e) {
    console.warn('Error pushing GTM identify event:', e);
  }
};

// Fetches the shop's add-on subscription status and returns the addon names
// (e.g. 'AI-powered-search') that are actually subscribed (i.e. not in `unsubscribedAddOns`).
// Add-ons only apply to the legacy subscription flow, so shops on subscription-v5 skip the call.
const fetchSubscribedAddonNames = async tenantApp => {
  const isSubscriptionV5 = tenantApp?.settings?.subscriptionV5?.settings?.enable || false;
  if (isSubscriptionV5) return [];

  try {
    const host = process.env.REACT_APP_ADMIN_V3_BASE_URL || 'https://staging.bc-solutions.net';
    const addonsUrl = host + '/boost-pfs-admin/admin/add-ons';
    const currentState = store.getState();
    const { authToken } = currentState.oauth;
    const response = await fetch(addonsUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken
      }
    });
    const { unsubscribedAddOns = [] } = await response.json();
    const unsubscribedNames = new Set(unsubscribedAddOns.map(addon => addon.name));

    return ADDONS_INFO_V4.map(addon => addon.addonName).filter(
      addonName => !unsubscribedNames.has(addonName)
    );
  } catch (error) {
    console.error('Warning fetching addon status:', { error: error.stack });
    return [];
  }
};

// Fetches the shop's invoice and returns its currently applied discount code
// (code, discountType/discountValue, expiredAt), or null if none is applied.
// `tenantApp.appliedDiscountCode` is never populated by the backend - the real data
// lives in the invoice response, same source the subscription pages use.
const fetchAppliedDiscountCode = async () => {
  try {
    const host = process.env.REACT_APP_ADMIN_V3_BASE_URL || 'https://staging.bc-solutions.net';
    const invoiceUrl = host + '/boost-pfs-admin/admin/recurring-application-charges/invoice';
    const currentState = store.getState();
    const { authToken } = currentState.oauth;
    const response = await fetch(invoiceUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken
      }
    });
    const { discountCodeApplied = {}, discountInfo = {} } = await response.json();
    if (!discountCodeApplied?.code) return null;

    return { ...discountCodeApplied, expiredAt: discountInfo?.expiredAt };
  } catch (error) {
    console.error('Warning fetching applied discount code:', { error: error.stack });
    return null;
  }
};

const decodeToken = async token => {
  try {
    const host = process.env.REACT_APP_ADMIN_V3_BASE_URL || 'https://staging.bc-solutions.net';
    const decodeTokenUrl = host + '/boost-pfs-admin/admin/decode-token';
    const currentState = store.getState();
    const { authToken } = currentState.oauth;
    const response = await fetch(`${decodeTokenUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken
      },
      body: JSON.stringify({ token })
    });
    const previewData = await response.json();
    return previewData;
  } catch (error) {
    console.error('Warning decoding token:', { error: error.stack });
    return {};
  }
};

export { initGtmIdentify };
